import { Component, computed, DestroyRef, HostListener, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import { OrderFilters } from './order-filters/order-filters';
import { OrderTable, LoadState, OrderListAction } from './order-table/order-table';
import {
  OrderFilters as OrderFiltersModel,
  OrderListItem,
} from '../../../../pages/admin-dashboard/orders-management/orders.model';
import {
  ORDER_STATUS_ID,
  ORDER_STATUS_TITLES,
} from '../../../../pages/admin-dashboard/orders-management/order-status.constants';
import { CancellationReason } from '../../../../pages/admin-dashboard/orders-management/view-order/view-order.model';
import { OrderService } from '../../../../services/orders/order.service';
import { ActionsService } from '../../../../services/actions/actions.service';
import { ReasonsService } from '../../../../services/reasons/reasons.service';
import { ClickOutsideDirective } from '../../pharmacies/admin-pharmacies-list/filter/utils/clickOutside';
import { UndeliverPopup } from '../view-order-details/actions/undelivered-popup/undeliver-popup/undeliver-popup';
import { CollectCashPopup } from '../view-order-details/actions/collect-cash-popup/collect-cash-popup';
import { OrderActionPopup } from '../../../../shared/ui/order-action-popup/order-action-popup';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';

interface ActiveAction {
  action: OrderListAction;
  orderId: string;
  order: OrderListItem;
}

@Component({
  selector: 'app-orders-list-component',
  standalone: true,
  imports: [
    OrderFilters,
    OrderTable,
    ClickOutsideDirective,
    UndeliverPopup,
    HasScopeDirective,
    OrderActionPopup,
    CollectCashPopup,
  ],
  templateUrl: './orders-list-component.html',
  styleUrl: './orders-list-component.scss',
})
export class OrdersListComponent implements OnInit {
  private actionsService = inject(ActionsService);
  private orderService = inject(OrderService);
  private reasonsService = inject(ReasonsService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);
  readonly SCOPES = SCOPES;

  state = signal<LoadState>('loading');
  orders = signal<OrderListItem[] | null>(null);
  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(25);
  totalItems = signal<number>(0);
  isFiltersApplied = signal<boolean>(false);

  filters = signal<OrderFiltersModel>({
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
  });

  activeAction = signal<ActiveAction | null>(null);
  submitting = signal(false);
  actionError = signal('');
  rejectionReasons = signal<any[]>([]);
  cancellationReasons = signal<CancellationReason[]>([]);

  otherCancellationReasonId = computed(() => {
    const reason = this.cancellationReasons().find(
      (item) => item.titleEn?.trim().toLowerCase() === 'other' || item.titleAr?.trim() === 'أخرى',
    );
    return reason?.id ?? null;
  });

  collectCashExpectedAmount = computed(() => Number((this.activeAction()?.order as any)?.amountCollected ?? 0));

  isUndeliverPopup = signal(false);
  selectedUndeliverOrderId = signal<number | null>(null);

  ngOnInit(): void {
    this.loadOrders();
  }

  loadOrders() {
    this.filters.update((f) => ({
      ...f,
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
    }));
    this.state.set('loading');

    const subscription = this.orderService.getOrders(this.filters()).subscribe({
      next: (res) => {
        this.orders.set(res.list);
        this.totalItems.set(res.total);
        this.state.set('success');
      },
      error: () => this.state.set('error'),
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems() / this.itemsPerPage());
  }

  toggleSortOrder() {
    this.filters.update((f) => ({
      ...f,
      orderDir: f.orderDir === 'ASC' ? 'DESC' : 'ASC',
    }));
    this.loadOrders();
  }

  applyFilters(applied: OrderFiltersModel) {
    this.currentPage.set(1);
    this.isFiltersApplied.set(true);
    this.filters.update((f) => ({ ...f, ...applied, pageNum: 0, pageSize: this.itemsPerPage() }));
    this.loadOrders();
  }

  resetFilters() {
    this.filters.set({ orderBy: 'LAST_MODIFICATION_DATE', orderDir: 'DESC' });
    this.isFiltersApplied.set(false);
    this.currentPage.set(1);
    this.loadOrders();
  }

  onOpenUndeliver(orderId: number) {
    this.selectedUndeliverOrderId.set(orderId);
    this.isUndeliverPopup.set(true);
  }

  onCloseUndeliver() {
    this.isUndeliverPopup.set(false);
    this.selectedUndeliverOrderId.set(null);
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      this.loadOrders();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadOrders();
  }

  selectPharmacyOwner(item: { id: number; fullName: string }) {
    this.filters.update((f) => ({ ...f, pharmacyOwnerId: item.id }));
  }

  navigateToDetails(id: number) {
    this.router.navigate(['/admin/orders/view-order', id]);
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }

  @HostListener('document:click')
  onDocumentClick() {}

  onOrderPrinted({ orderId, statusId }: { orderId: string; statusId: number }) {
    this.updateOrderStatus(orderId, statusId);
  }

  onOrderAction(event: { action: OrderListAction; order: OrderListItem }) {
    const { action, order } = event;

    this.actionError.set('');
    this.activeAction.set({ action, order, orderId: String(order.id) });

    if (action === 'reject-review') {
      this.loadRejectionReasons();
    }
    if (action === 'cancel') {
      this.loadCancellationReasons();
    }
  }

  closeAction() {
    this.activeAction.set(null);
    this.actionError.set('');
    this.submitting.set(false);
  }

  onConfirmReview(comment: string) {
    const id = this.activeAction()?.orderId;
    if (!id) return;
    this.execute(this.actionsService.confirmReview(id, comment), () =>
      this.updateOrderStatus(id, ORDER_STATUS_ID.REVIEW_APPROVED),
    );
  }

  onRejectReview(data: { reasonId: number; otherReason: string; comment: string }) {
    const id = this.activeAction()?.orderId;
    if (!id) return;
    const payload = {
      reason: { reasonId: data.reasonId, otherReason: data.otherReason },
      comment: data.comment,
    };
    this.execute(this.orderService.rejectOrder(id, payload), () =>
      this.updateOrderStatus(id, ORDER_STATUS_ID.REVIEW_REJECTED),
    );
  }

  onShip(comment: string) {
    const id = this.activeAction()?.orderId;
    if (!id) return;
    this.execute(this.actionsService.shipOrder(id, comment), () => this.updateOrderStatus(id, ORDER_STATUS_ID.SHIPPED));
  }

  onRepackage(comment: string) {
    const id = this.activeAction()?.orderId;
    if (!id) return;
    this.execute(this.orderService.repackageOrder(id, comment), () =>
      this.updateOrderStatus(id, ORDER_STATUS_ID.PACKAGED),
    );
  }

  onReceiveReturned(comment: string) {
    const id = this.activeAction()?.orderId;
    if (!id) return;
    this.execute(this.orderService.receiveReturnedProducts(id, comment), () => this.loadOrders());
  }

  onCollectCash(payload: { actualAmount: number; comment: string }) {
    const id = this.activeAction()?.orderId;
    if (!id) return;
    this.execute(
      this.orderService.collectOrderCash(id, {
        amountCollected: payload.actualAmount,
        comment: payload.comment,
      }),
      () => this.loadOrders(),
    );
  }

  onCancelOrder(data: { reasonId: number; otherReason: string; comment: string }) {
    const id = this.activeAction()?.orderId;
    if (!id) return;
    this.execute(
      this.orderService.cancelOrder(id, {
        reason: { otherReason: data.otherReason, reasonId: data.reasonId },
        comment: data.comment,
      }),
      () => this.updateOrderStatus(id, ORDER_STATUS_ID.CANCELLED),
    );
  }

  private execute(request$: Observable<unknown>, onSuccess: () => void) {
    this.submitting.set(true);
    this.actionError.set('');

    const subscription = request$.pipe(finalize(() => this.submitting.set(false))).subscribe({
      next: () => {
        onSuccess();
        this.closeAction();
      },
      error: (err) => this.actionError.set(this.extractError(err)),
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private loadRejectionReasons() {
    const subscription = this.reasonsService.getRejectionReasons().subscribe({
      next: (res: any) => this.rejectionReasons.set(res.list ?? []),
      error: (err) => this.actionError.set(this.extractError(err)),
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private loadCancellationReasons() {
    const subscription = this.reasonsService.getCancellationReasons().subscribe({
      next: (res) => this.cancellationReasons.set(res.list),
      error: (err) => this.actionError.set(this.extractError(err)),
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private updateOrderStatus(orderId: string | number, statusId: number) {
    const titles = ORDER_STATUS_TITLES[statusId];
    if (!titles) return;
    this.orders.update((list) =>
      list
        ? list.map((order) =>
            +order.id === +orderId
              ? {
                  ...order,
                  status: { id: statusId, titleEn: titles.titleEn, titleAr: titles.titleAr, profileImageFid: '' },
                }
              : order,
          )
        : list,
    );
  }

  private extractError(error: any): string {
    const body = error?.error ?? error;

    const message = this.isArabic()
      ? (body?.messageAr ?? error?.messageAr ?? body?.message)
      : (body?.messageEn ?? error?.messageEn ?? body?.message);

    if (message) return message;

    return this.isArabic() ? 'حدث بعض الخطأ' : 'some error occured!';
  }
}
